import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import {
  faCaretDown,
  faFileArrowUp,
  faPlus,
  faTags
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Drawer from '@mui/material/Drawer';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useAtom } from 'jotai';
import {
  bindMenu,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import CheckPermissions from '@/components/CheckPermissions';
import { ExportButton } from '@/components/ExportButton';
import ResizablePanel from '@/components/ResizablePanel';
import { PERMISSIONS } from '@/constants/auth.constants';
import { EXPORT_COLUMNS_ASSETS } from '@/constants/exportcolumns.constants';
import AssetAddEditDialog from '@/features/AssetAddEditDialog';
import AssetMappingUploadDialog from '@/features/AssetMappingUploadDialog';
import AssetPanel from '@/features/AssetPanel';
import AssetRiskScoreDialog from '@/features/AssetRiskScoreDialog';
import AssetSearch from '@/features/AssetSearch';
import AssetTable from '@/features/AssetTable';
import AssetUploadDialog from '@/features/AssetUploadDialog';
import Header from '@/layout/Header/Header';
import MainContent from '@/layout/MainContent';
import {
  assetRiskScoreDialogAtom,
  assetsTableOrderAtom,
  assetsTableOrderByAtom,
  filteredAssetsAtom,
  isAssetsPanelOpenAtom,
  mapAssetCsvData,
  selectedAssetAtom,
  selectedAssetIdAtom,
  useAssetSelection,
  useFetchAssetDetails,
  useFetchAssets
} from '@/pages/Assets';
import useStyles from '@/pages/Assets/Assets.styles';
import { sortExportColumnsData } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.utils';
import { isLayoutMobileAtom } from '@/store/layout';

import { selectedOrganizationAtom } from '../Organizations';

const Assets = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const { deselectAsset, selectAsset } = useAssetSelection();
  const [assetsTableOrder] = useAtom(assetsTableOrderAtom);
  const [assetsTableOrderBy] = useAtom(assetsTableOrderByAtom);
  const [filteredAssets] = useAtom(filteredAssetsAtom);
  const [isAssetsPanelOpen, setIsAssetsPanelOpen] = useAtom(
    isAssetsPanelOpenAtom
  );
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [selectedAssetId] = useAtom(selectedAssetIdAtom);
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const { isFetching, isLoading, refetch: refetchAssets } = useFetchAssets();
  const { refetch: refetchAssetDetails } = useFetchAssetDetails();
  const [searchParams] = useSearchParams();
  const { classes } = useStyles();

  const [assetRiskScoreDialog] = useAtom(assetRiskScoreDialogAtom);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openMappingUploadDialog, setOpenMappingUploadDialog] = useState(false);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  const uploadButtonAnchorRef = useRef<HTMLButtonElement>(null);
  const popupState = usePopupState({
    popupId: 'uploadMenu',
    variant: 'popover'
  });

  const sizes = ['75%', '25%'];

  const isPanelOpen = isAssetsPanelOpen && !isLayoutMobile;

  const panels = useMemo(
    () => [
      {
        content: <AssetTable isLoading={isFetching} />,
        id: 'assets-table',
        order: 1,
        minSize: isPanelOpen ? '40%' : '0%',
        maxSize: isPanelOpen ? '75%' : '100%',
        showResizeHandle: isPanelOpen
      },
      {
        content: <AssetPanel notAssetDetailsPage />,
        id: 'assets-details',
        order: 2,
        minSize: '25%',
        maxSize: '40%',
        showResizeHandle: false
      }
    ],
    [isPanelOpen, isFetching]
  );

  const handleAddAsset = () => {
    setOpenAddDialog(true);
  };

  const handleUploadSelection = (selection: 'assets' | 'mapping') => {
    popupState.close();
    if (selection === 'assets') {
      setOpenUploadDialog(true);
    } else if (selection === 'mapping') {
      setOpenMappingUploadDialog(true);
    }
  };

  const handleDrawerClose = () => {
    deselectAsset();
  };

  const handleRefresh = () => {
    refetchAssets();
    if (selectedAssetId) {
      refetchAssetDetails();
    }
  };

  const exportColumnsData = useMemo(() => {
    const assetExportColumns = Object.keys(EXPORT_COLUMNS_ASSETS);
    return sortExportColumnsData(
      filteredAssets,
      assetExportColumns,
      assetExportColumns[0],
      assetsTableOrderBy ?? assetExportColumns[0],
      assetsTableOrder ?? 'asc'
    );
  }, [filteredAssets, assetsTableOrderBy, assetsTableOrder]);

  useEffect(() => {
    // on load - if asset param exist in the url - select asset
    const assetId = searchParams.get('asset');
    if (
      assetId &&
      !isLoading &&
      filteredAssets.length > 0 &&
      filteredAssets.find((a) => a.id === Number(assetId))
    ) {
      selectAsset(assetId);
    }

    // if there is assetId param in the url or selectedAsset but it is not in the filtered assets - deselect asset
    // this check must be debounced because on initial page load - loading is false when data is loaded but filteredAssets is still empty and is set in the next tick
    const handler = setTimeout(() => {
      if ((assetId || selectedAsset) && !isLoading) {
        const id = assetId || selectedAsset?.id;
        if (!filteredAssets.find((a) => a.id === Number(id))) {
          deselectAsset();
        }
      }
    }, 100);

    return () => {
      clearTimeout(handler);
    };
  }, [filteredAssets, isLoading]);

  useEffect(() => {
    // on selected asset change - open or close the asset details panel
    if (selectedAsset) {
      setIsAssetsPanelOpen(true);
      return;
    }
    setIsAssetsPanelOpen(false);
  }, [selectedAsset]);

  useEffect(() => {
    // on selected asset id change - fetch asset details if selecteAssetId is set
    if (typeof selectedAssetId === 'string' && selectedAssetId) {
      refetchAssetDetails();
    }
  }, [selectedAssetId]);

  return (
    <>
      <Header
        title="Assets"
        actions={
          <CheckPermissions permissions={PERMISSIONS.ASSET_UPDATE}>
            <ExportButton
              data={exportColumnsData}
              fileName="pondurance-assets"
              json
              csv
              mapCsvData={mapAssetCsvData}
            />
            <Button onClick={() => handleRefresh()} variant="outlined">
              <FontAwesomeIcon size="lg" icon={faRefresh} />
            </Button>
            <Button
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon={faPlus} size="xs" />}
              sx={{ whiteSpace: 'nowrap' }}
              onClick={handleAddAsset}
              disabled={selectedOrganization.label === 'all'}
            >
              Add Asset
            </Button>
            <ButtonGroup
              variant="contained"
              disabled={selectedOrganization.label === 'all'}
            >
              <Button
                ref={uploadButtonAnchorRef}
                sx={{ whiteSpace: 'nowrap' }}
                {...bindTrigger(popupState)}
              >
                Upload
              </Button>
              <Button size="small" {...bindTrigger(popupState)}>
                <FontAwesomeIcon icon={faCaretDown} size="1x" />
              </Button>
            </ButtonGroup>
            <Menu
              {...bindMenu(popupState)}
              anchorEl={uploadButtonAnchorRef.current}
            >
              <MenuItem onClick={() => handleUploadSelection('assets')}>
                <FontAwesomeIcon
                  icon={faFileArrowUp}
                  size="xs"
                  style={{ marginRight: '8px' }}
                />
                <Typography variant="subtitle2">Upload Assets</Typography>
              </MenuItem>
              <MenuItem onClick={() => handleUploadSelection('mapping')}>
                <FontAwesomeIcon
                  icon={faTags}
                  size="xs"
                  style={{ marginRight: '8px' }}
                />
                <Typography variant="subtitle2">
                  Upload IP-Tag Mapping
                </Typography>
              </MenuItem>
            </Menu>
          </CheckPermissions>
        }
      >
        <Box className={classes.assetsSearchWrapper}>
          <AssetSearch />
        </Box>
      </Header>
      <MainContent>
        <Box className={classes.assetsResizablePanelWrapper}>
          <ResizablePanel
            panelGroupKey={isPanelOpen ? 'two-panels' : 'one-panel'}
            sizes={isPanelOpen ? sizes : ['100%']}
            panels={isPanelOpen ? panels : [panels[0]]}
          />
        </Box>
        {isLayoutMobile && (
          <Drawer
            anchor="right"
            open={isAssetsPanelOpen && isLayoutMobile}
            onClose={handleDrawerClose}
            slotProps={{
              paper: { sx: { width: '75vw' } }
            }}
          >
            <AssetPanel notAssetDetailsPage />
          </Drawer>
        )}
      </MainContent>
      {openAddDialog && (
        <AssetAddEditDialog
          open={openAddDialog}
          onClose={() => setOpenAddDialog(false)}
          type="add"
        />
      )}
      {openUploadDialog && (
        <AssetUploadDialog
          open={openUploadDialog}
          onClose={() => setOpenUploadDialog(false)}
        />
      )}
      {openMappingUploadDialog && (
        <AssetMappingUploadDialog
          open={openMappingUploadDialog}
          onClose={() => setOpenMappingUploadDialog(false)}
        />
      )}
      {assetRiskScoreDialog && <AssetRiskScoreDialog />}
    </>
  );
};

export default Assets;
